import { useContext, useEffect, useRef } from "react";
import { Navigate, useLocation } from "react-router";
import {
  AUTH_SESSION_STATUS,
  AuthSessionContext,
} from "../../components/contexts/authSessionContext";
import { PageTitleContext } from "../../components/contexts/pageTitleContext";
import { AUTH_ROUTE_PATHS } from "../../services/auth/authRouteConfig";
import { resolveTrackedPageTitle } from "../../services/pageTitleResolver";
import { postUserActivityToRDS } from "../../services/userActivityService";
import { rememberDesiredNav } from "../../services/auth/desiredNavService";

// Guards authenticated routes. Renders children when authenticated, redirects
// to /auth/check when unauthenticated, and renders nothing while loading
// (including during OIDC callback settling).
const PrivateRoute = ({ children }) => {
  const { status } = useContext(AuthSessionContext);
  const pageTitleContext = useContext(PageTitleContext);
  const getPageTitle = pageTitleContext?.getPageTitle;
  const pageTitle = pageTitleContext?.pageTitle || "";
  const location = useLocation();
  const prevLocationRef = useRef("");

  useEffect(() => {
    if (status !== AUTH_SESSION_STATUS.UNAUTHENTICATED) {
      return;
    }

    // Build URL from React Router's location snapshot rather than
    // globalThis.location.href — by the time this effect runs, the sibling
    // <Navigate> effect has already changed the browser URL to /auth/check.
    const url = `${globalThis.location.origin}${location.pathname}${location.search}${location.hash}`;
    rememberDesiredNav(url);
  }, [status, location]);

  useEffect(() => {
    if (status !== AUTH_SESSION_STATUS.AUTHENTICATED) {
      return;
    }

    const path = location.pathname;
    if (path !== prevLocationRef.current.pathname) {
      // Prefer the explicit title registered by the screen. If a screen does
      // not set one yet, derive a readable fallback from the route so every
      // tracked navigation still has a useful label.
      const fallbackTitle = resolveTrackedPageTitle(path);
      requestAnimationFrame(() => {
        const resolvedTitle = getPageTitle?.() || fallbackTitle;
        postUserActivityToRDS(path, {
          pageTitle: resolvedTitle,
        });
      });
    }

    prevLocationRef.current = location;
  }, [location, pageTitle, status]);

  switch (status) {
    case AUTH_SESSION_STATUS.AUTHENTICATED:
      return children;
    case AUTH_SESSION_STATUS.UNAUTHENTICATED:
      return <Navigate to={AUTH_ROUTE_PATHS.AUTH_CHECK} replace />;
    case AUTH_SESSION_STATUS.LOADING:
    default:
      return null;
  }
};

export default PrivateRoute;
